<template>
  <div class="frisbuy-pagination-panel">
    <div class="frisbuy-pagination-panel__inner" ref="paginationPanelInner">
      <ArrowButton
        customClass="frisbuy-pagination-panel__arrow"
        direction="left"
        fillColor="#303133"
        fillColorDisabled="#C0C4CC"
        :isDisabled="isFirstPage"
        @click="() => selectPage(props.selectedPage - 1)"
      />

      <div class="frisbuy-pagination-panel__pages">
        <button
          class="frisbuy-pagination-panel__page"
          :class="{ '--selected': 1 === props.selectedPage }"
          @click="() => selectPage(1)"
        >
          1
        </button>

        <ButtonHiddenPages
          v-if="isVisibleLeftButtonHiddenPages"
          class="frisbuy-pagination-panel__page"
          direction="left"
          @click="
            selectPage(
              props.selectedPage -
                (visiblePagesCount - constantlyVisiblePagesCount)
            )
          "
        />

        <template v-for="page in visiblePages" :key="page">
          <button
            class="frisbuy-pagination-panel__page"
            :class="{ '--selected': page === props.selectedPage }"
            @click="() => selectPage(page)"
          >
            {{ page }}
          </button>
        </template>

        <ButtonHiddenPages
          v-if="isVisibleRightButtonHiddenPages"
          class="frisbuy-pagination-panel__page"
          direction="right"
          @click="
            selectPage(
              props.selectedPage +
                (visiblePagesCount - constantlyVisiblePagesCount)
            )
          "
        />

        <button
          v-if="props.countOfPages > 1"
          class="frisbuy-pagination-panel__page"
          :class="{ '--selected': props.countOfPages === props.selectedPage }"
          @click="() => selectPage(props.countOfPages)"
        >
          {{ props.countOfPages }}
        </button>
      </div>

      <ArrowButton
        customClass="frisbuy-pagination-panel__arrow"
        direction="right"
        fillColor="#303133"
        fillColorDisabled="#C0C4CC"
        :isDisabled="isLastPage"
        @click="() => selectPage(props.selectedPage + 1)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.frisbuy-pagination-panel {
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .frisbuy-pagination-panel__inner {
    display: flex;
    flex-direction: row;

    position: relative;
    width: fit-content;

    margin: 0 auto;

    .frisbuy-pagination-panel__arrow {
      position: relative;

      width: 32px;
      height: 32px;

      inset: 0;
      transform: none;

      padding: 9px;
    }

    .frisbuy-pagination-panel__pages {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;

      .frisbuy-pagination-panel__page {
        background: transparent;
        border: none;

        color: #606266;
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 22px;
        text-align: center;

        width: fit-content;
        height: 32px;
        min-width: 32px;

        cursor: pointer;

        transition: all 0.2s linear;

        &:hover {
          border-radius: 2px;

          background-color: #{#606266}20; // opacity 32%;
        }

        &.--selected {
          color: #000000;
        }
      }
    }
  }
}
</style>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import ArrowButton from "../ArrowButton.vue";
import ButtonHiddenPages from "./ButtonHiddenPages.vue";

const props = defineProps({
  countOfPages: {
    type: Number,
    required: true,
    validator: (val: number) => val > 0,
  },

  maxVisiblePages: {
    type: Number,
    required: false,
    default: 8,
    validator: (val: number) => val > 0,
  },

  selectedPage: {
    type: Number,
    required: true,
    validator: (val: number) => val >= 1,
  },
});

const emit = defineEmits<{
  selectPage: [page: number];
}>();

const isFirstPage = computed(() => {
  return props.selectedPage === 1;
});
const isLastPage = computed(() => {
  return props.selectedPage === props.countOfPages;
});

function selectPage(page: number) {
  let pageForEmit = page;

  if (pageForEmit < 1) {
    pageForEmit = 1;
  } else if (pageForEmit > props.countOfPages) {
    pageForEmit = props.countOfPages;
  }

  if (pageForEmit === props.selectedPage) {
    return;
  }

  emit("selectPage", pageForEmit);
}

//#region Adaptive pages count
const visiblePagesCount = ref<number>(props.maxVisiblePages);
const minVisiblePagesCount = 3;
const paginationPanelInner = ref<HTMLElement>();

onMounted(() => {
  recompileVisiblePagesCount();
  window.addEventListener("resize", recompileVisiblePagesCount);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", recompileVisiblePagesCount);
});

async function recompileVisiblePagesCount() {
  await nextTick();
  const scrollWidth = 45;
  const windowWidth = window.innerWidth - scrollWidth;

  if (
    paginationPanelInner.value &&
    paginationPanelInner.value.offsetWidth > windowWidth &&
    visiblePagesCount.value > minVisiblePagesCount + 1
  ) {
    visiblePagesCount.value -= 1;
    recompileVisiblePagesCount();
  } else if (
    paginationPanelInner.value &&
    paginationPanelInner.value.offsetWidth < windowWidth &&
    visiblePagesCount.value <= props.maxVisiblePages
  ) {
    visiblePagesCount.value += 1;
    // recompileVisiblePagesCount();
  }
}
//#endregion Adaptive pages count

const visiblePages = computed<Array<number>>(() => {
  const pages = [];

  for (let i = minVisiblePage.value; i <= maxVisiblePage.value; i++) {
    if (getPageVisibility(i)) {
      pages.push(i);
    }
  }

  return pages;
});

const constantlyVisiblePagesCount = 2; // NOTE: First and Last page constantly visible
const minVisiblePage = computed(() => {
  return Math.ceil(
    props.selectedPage -
      (visiblePagesCount.value - constantlyVisiblePagesCount) / 2
  );
});
const maxVisiblePage = computed(() => {
  return minVisiblePage.value > 0
    ? minVisiblePage.value +
        visiblePagesCount.value -
        constantlyVisiblePagesCount
    : visiblePagesCount.value + 1; // NOTE: 1 - first page
});

function getPageVisibility(page: number) {
  if (page <= 0 || page > props.countOfPages) {
    return false;
    // throw new Error(`Wrong page (${page}). 0 < ${page} <= countOfPages`);
  }

  const isVisible =
    page <= maxVisiblePage.value && page >= minVisiblePage.value;

  const isFirstPage = page === 1;
  const isLastPage = page === props.countOfPages;

  return isVisible && !isFirstPage && !isLastPage;
}

const isVisibleLeftButtonHiddenPages = computed(() => {
  const isVisible =
    minVisiblePage.value > 2 ? getPageVisibility(minVisiblePage.value) : false;

  return isVisible;
});
const isVisibleRightButtonHiddenPages = computed(() => {
  const isVisible = maxVisiblePage.value < props.countOfPages;

  return isVisible;
});
</script>
